import { useState, useEffect } from 'react'
import { AlertTriangle } from 'lucide-react'

interface MaintenanceNoticeProps {
  message: string
  endDateTime?: string | null
}

export default function MaintenanceNotice({ message, endDateTime }: MaintenanceNoticeProps) {
  const [timeRemaining, setTimeRemaining] = useState<string>('')

  useEffect(() => {
    if (!endDateTime) return

    const updateCountdown = () => {
      const now = new Date().getTime()
      const end = new Date(endDateTime).getTime()
      const distance = end - now

      if (distance < 0) {
        setTimeRemaining('Maintenance should be ending shortly...')
        return
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24))
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((distance % (1000 * 60)) / 1000)

      let timeStr = ''
      if (days > 0) timeStr += `${days}d `
      if (hours > 0) timeStr += `${hours}h `
      if (minutes > 0) timeStr += `${minutes}m `
      timeStr += `${seconds}s`

      setTimeRemaining(timeStr.trim())
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)

    return () => clearInterval(interval)
  }, [endDateTime])

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-50 border-b-2 border-yellow-400">
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <div className="text-center">
              <p className="text-sm font-medium text-yellow-800">
                {message}
              </p>
              {endDateTime && timeRemaining && (
                <p className="text-xs text-yellow-700 mt-1">
                  Estimated time remaining: <span className="font-mono font-semibold">{timeRemaining}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
